import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { parseError } from 'hooks/shared/error';
import { toast } from 'react-toastify';
import {
  noteScopeKey,
  noteScopeParams,
  type Note,
  type NoteScope,
} from './notesModel';

export const notesKeys = {
  all: ['notes'] as const,
  list: (scope: NoteScope | null) =>
    [...notesKeys.all, noteScopeKey(scope)] as const,
};

function notesError(error: unknown): string {
  const parsed = parseError(error);
  if (parsed) return parsed;
  const data = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data?.error;
  if (typeof data === 'string' && data.trim()) return data;
  return 'Something went wrong';
}

export function useNotes(scope: NoteScope | null) {
  return useQuery({
    queryKey: notesKeys.list(scope),
    enabled: Boolean(scope),
    queryFn: async () => {
      const response = await axios.get<Note[]>(config.apiGateway.notes, {
        params: noteScopeParams(scope as NoteScope),
      });
      return response.data ?? [];
    },
  });
}

export function useCreateNote(scope: NoteScope | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; body?: string }) => {
      const response = await axios.post<Note>(config.apiGateway.notes, {
        ...noteScopeParams(scope as NoteScope),
        ...input,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKeys.list(scope) });
    },
    onError: (error) => toast.error(notesError(error)),
  });
}

export function useUpdateNote(scope: NoteScope | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...values
    }: {
      id: string;
      title?: string;
      body?: string;
      shared?: boolean;
    }) => {
      const response = await axios.put<Note>(
        `${config.apiGateway.notes}/${id}`,
        values
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKeys.list(scope) });
    },
    onError: (error) => toast.error(notesError(error)),
  });
}

export function useDeleteNote(scope: NoteScope | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${config.apiGateway.notes}/${id}`);
      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notesKeys.list(scope) });
    },
    onError: (error) => toast.error(notesError(error)),
  });
}
